import React from "react";
import SeoPage from "../../components/SeoPage";
import RELATED from "../../data/related";
import { faqsByTopic } from "../../data/faqs";

export default function OpenCsvFiles() {
    return (
        <SeoPage
            title="How to Open CSV Files — On Any Device"
            description="Three simple ways to open a CSV file: in OpenCSV (instant, no install), in a spreadsheet app, or in a text editor. Step-by-step guide."
            breadcrumbs={[{ label: "Home", href: "/" }, { label: "Open CSV Files" }]}
            eyebrow="/ how to"
            h1="How to open a CSV file"
            heroLead="There's no single 'right' way to open a CSV file — it depends on what you're doing. Here are the three options, when to pick each one, and a 30-second walkthrough for each."
            heroBullets={["Open in a CSV viewer (fastest, no install)", "Open in a spreadsheet app (Excel / Numbers)", "Open in a text editor (raw view)"]}
            sections={[
                { heading: "Option 1 — Open in a CSV viewer (recommended)",
                    body: "A dedicated CSV viewer like OpenCSV opens any .csv file in your browser instantly. Nothing to install, nothing converts your data, and it works the same on phone, tablet, and laptop.",
                    sub: [
                        { heading: "Steps", bullets: [
                            "Go to opencsv.io",
                            "Click 'Open the editor' or drag your CSV file into the page",
                            "Your data renders in a clean, scrollable grid",
                        ]},
                    ],
                    callout: "Best for: quick previews, mobile use, large exports, anyone who doesn't have spreadsheet software handy."
                },
                { heading: "Option 2 — Open in a spreadsheet app",
                    body: "Excel, Numbers, and Google Sheets all open CSV files. They give you formulas and charts, but they can also reformat your data: dates get converted, leading zeros disappear, very long numbers turn into scientific notation.",
                    sub: [
                        { heading: "Excel (Windows / Mac)", bullets: ["Right-click the file → Open with → Excel", "Or: open Excel, File → Open → choose your .csv"]},
                        { heading: "Numbers (Mac / iOS)", bullets: ["Double-click the .csv file", "Or: Numbers → File → Open → pick your .csv"]},
                        { heading: "Google Sheets", bullets: ["Open Google Sheets → File → Import → Upload", "Choose 'Replace spreadsheet' to keep CSV formatting clean"]},
                    ],
                    callout: "Watch for: ZIP-code columns becoming numbers (drops the leading zero), dates getting reformatted, and 16-digit numbers turning into 1.23E+15."
                },
                { heading: "Option 3 — Open in a text editor",
                    body: "Because CSV is plain text, any text editor will show you the raw file: Notepad on Windows, TextEdit on Mac, VS Code, Sublime, etc. Useful for spotting weird characters, encoding problems, or non-comma delimiters.",
                    sub: [
                        { heading: "When to use it", bullets: ["You suspect the file has a different delimiter (tabs, semicolons)", "Some characters look corrupted (encoding issue)", "You want to do a find-and-replace at the file level", "The file is too large to open in any other tool"]},
                    ],
                },
                { heading: "Which one should you use?",
                    table: {
                        headers: ["You want to…", "Use"],
                        rows: [
                            ["Quickly look at the data", "OpenCSV"],
                            ["Edit a few cells, save back", "OpenCSV"],
                            ["Run formulas, build a pivot table", "Excel / Sheets"],
                            ["Inspect a corrupt or unusual file", "Text editor"],
                            ["Open a really big file (100k+ rows)", "OpenCSV"],
                            ["Open on a phone with no apps installed", "OpenCSV"],
                        ],
                    },
                },
            ]}
            faqs={faqsByTopic("open", "general", "large-files", "delimiters")}
            related={[RELATED.viewer, RELATED.what, RELATED.edit, RELATED.vs, RELATED.fast, RELATED.faq]}
        />
    );
}
